/**
 * Application bootstrap.
 *
 * Responsibilities, in order:
 *   1. load persisted state (never throws - falls back to defaults),
 *   2. build the observable store and the single `actions` object every
 *      component talks to,
 *   3. mount the app shell,
 *   4. keep LocalStorage and the theme in sync with state.
 *
 * All business logic lives behind `src/scheduler/index.js`; all persistence
 * behind `src/io/storage.js`. This file only wires them together.
 */

import './styles/index.css';

import { createStore } from './ui/store.js';
import { createApp } from './ui/app.js';
import { createActions } from './ui/actions.js';
import { debounce } from './ui/dom.js';
import { isStorageAvailable, loadAppState, saveAppState } from './io/storage.js';

/** Fields that exist only for this browser session and are never persisted. */
function createRuntimeState(storageAvailable) {
  return {
    saveStatus: storageAvailable ? 'saved' : 'unavailable',
    saveError: null,
    storageAvailable,
    dialog: null,
    dialogPayload: null,
    generating: false,
    dragging: null,
    /** Bumped whenever a component must force a full re-render. */
    revision: 0,
  };
}

function applyTheme(theme) {
  const resolved = theme === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;
  document.documentElement.dataset.theme = resolved;
  return resolved;
}

function start() {
  const storageAvailable = isStorageAvailable();
  const persisted = loadAppState();

  const store = createStore({
    ...persisted,
    runtime: createRuntimeState(storageAvailable),
  });

  applyTheme(store.getState().theme);

  // Follow the OS when the user has chosen "system".
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  media.addEventListener('change', () => {
    if (store.getState().theme === 'system') applyTheme('system');
  });

  const actions = createActions(store);

  // --- Persistence -------------------------------------------------------
  // Debounced so typing in the rota editor does not hammer LocalStorage, and
  // flushed on pagehide so nothing is lost when the tab closes.
  const persist = debounce(() => {
    if (!storageAvailable) return;
    const { runtime: _runtime, ...persistable } = store.getState();
    const result = saveAppState(persistable);
    store.setState(
      (state) => ({
        runtime: {
          ...state.runtime,
          saveStatus: result.ok ? 'saved' : 'error',
          saveError: result.ok ? null : result.error,
        },
      }),
      { reason: 'persist' },
    );
  }, 450);

  store.subscribe((_state, meta) => {
    if (meta?.reason === 'persist' || meta?.reason === 'runtime') return;
    if (!storageAvailable) return;
    store.setState(
      (state) => (state.runtime.saveStatus === 'saving'
        ? null
        : { runtime: { ...state.runtime, saveStatus: 'saving' } }),
      { reason: 'runtime' },
    );
    persist();
  });

  // Repair the exact two-page benchmark PDF affected by the old row-merging
  // bug before auditing schedules. Every other rota is left untouched, and an
  // unreadable rota is never used to destructively replace a fuller schedule.
  const recoveredPdf = actions.recoverCorruptBenchmarkPdfIfNeeded();
  if (!recoveredPdf) actions.refreshScheduleIfNeeded();

  window.addEventListener('pagehide', () => persist.flush());

  // --- Mount -------------------------------------------------------------
  const root = document.getElementById('app');
  const app = createApp({ store, actions });
  root.appendChild(app.element);
  app.update(store.getState());
  store.subscribe((state) => app.update(state));

  // Surface unexpected failures instead of leaving a silently broken screen.
  window.addEventListener('error', (event) => {
    actions.toast({ message: `Something went wrong: ${event.message}`, tone: 'danger' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.message ?? String(event.reason ?? 'Unknown error');
    actions.toast({ message: `Something went wrong: ${reason}`, tone: 'danger' });
  });

  if (!storageAvailable) {
    actions.toast({
      message: 'Browser storage is unavailable, so nothing will be saved between visits.',
      tone: 'warn',
      duration: 9000,
    });
  }
}

start();
